import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import prettyBytes from 'pretty-bytes';
import { useNavigate } from 'react-router-dom';

import { Project } from '../../../app/entities/project';
import { tabPool } from '../../../features/tabs/models/tabs-pool';
import { timeDifference } from '../../../lib/utils/TimeDifference';
import { formatDate } from '../../../lib/utils/DateHelper';
import { ListViewItem } from '../../../components/list-view/list-view';
import { useResponsive } from '../../../hooks/useResponsive';
import { CoverImage } from '../../../components/project-item/CoverImage';
import { ProjectMenu } from '../../assets/components/project-menu';
import { PrioritySelector } from '../../process/components/priority-selector';
import { useCurrentMember } from '../../../hooks/use-current-member';
import { Collaborators } from '@producer/ui-kit';
import { openModal } from '../../../core/modal/open-modal';
import { ProjectCollaboratorModal } from '../modals/project-collaborator.modal';
import { Member } from '../../../app/entities/member';

interface ProjectListItemProps {
  project: Project;
}

export const ProjectListItem: React.FC<ProjectListItemProps> = observer(({ project }) => {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();

  const currentMember = useCurrentMember();

  const archivedByMember = useMemo(() => {
    if (!project?.archivedById) return null;
    return Member.getOne(project.archivedById);
  }, [project?.archivedById]);

  const handleOpenProject = (e: React.MouseEvent) => {
    const href = `/space/${project.spaceId}/projects/${project._id}`;

    tabPool.newTab({
      key: project._id,
      label: project.name,
      href,
    });

    if (!e.shiftKey) {
      navigate(href);
    }
  };

  return (
    <StyledListViewItem onClick={handleOpenProject} $archived={project.archived}>
      <ProjectAvatarWrapper>
        <CoverImage key={project.avatar} src={project.avatar || ''} title={project.name} />
      </ProjectAvatarWrapper>
      <td style={{ width: '100%' }}>
        {project.name}
        {project.archived && project.archivedAt && (
          <ArchivedLabel>
            {' '}
            · Archived {formatDate(project.archivedAt.toString(), 'MMM dd, yyyy')}
            {archivedByMember && ` by ${archivedByMember.fullName}`}
          </ArchivedLabel>
        )}
      </td>
      <td style={{ textTransform: 'uppercase' }}>{project.progress}%</td>
      <td>
        {project.usedStorage !== null && project.usedStorage !== undefined
          ? prettyBytes(project.usedStorage)
          : ''}
      </td>
      <td>{project.lastUpdatedAt ? timeDifference(new Date(), project.lastUpdatedAt) : ''}</td>
      <td>{project.dueDate ? formatDate(project.dueDate.toString(), 'MMM dd, yyy') : ''}</td>
      <td>
        <PrioritySelector
          priority={project.priority}
          onChange={(priority) => {
            project.update({ priority });
          }}
          withTitle
        />
      </td>
      {isDesktop && ['owner', 'admin', 'creator'].includes(currentMember?.role || '') && (
        <td>
          <Collaborators
            collaborators={project.collaborators}
            onClick={() =>
              openModal(ProjectCollaboratorModal, {
                project,
              })
            }
          />
        </td>
      )}
      <td />
      <td style={{ paddingRight: '0.8rem', width: '4.8rem' }}>
        <LastCellWrapper>
          <ProjectMenu project={project} />
        </LastCellWrapper>
      </td>
    </StyledListViewItem>
  );
});

const ProjectAvatarWrapper = styled.td`
  padding-right: 1.6rem !important;

  img {
    position: relative;
    width: 5.6rem;
    height: 3.2rem;
    border-radius: 0.2rem;
  }
`;

const LastCellWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 4.8rem;
`;

const StyledListViewItem = styled(ListViewItem)<{ $archived?: boolean }>`
  opacity: ${({ $archived }) => ($archived ? 0.6 : 1)};
  transition: opacity 0.2s ease;
`;

const ArchivedLabel = styled.span`
  color: #ffa500;
  font-style: italic;
  font-size: 1.2rem;
`;
